import { generatePath } from 'react-router-dom';

import apiInstance from '@/components/shared/api';

import { APIRoutes } from '@/components/shared/routes';
import { Config } from '@/components/shared/utils/config';

export interface Assignee {
    id: string;
    applicantId: string;
    email: string;
    product?: string;
    createdBy: string;
    dateCreated: string;
}

export interface AssigneeRequest {
    applicantId: string;
    email: string;
    product?: string;
}

export interface DeleteAssigneeRequest {
    applicantId: string;
    assigneeId: string;
}

export async function getApplicantAssignees(applicantId: string): Promise<Assignee[]> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_ASSIGNEES, {
        applicantId,
    });
    return (
        await apiInstance.get<Assignee[]>(endpointUrl, {
            baseURL: `${Config.BaseApiUrl}/originations-review-portal`,
        })
    ).data;
}

// Batch lookup so the queue can refresh assignees for every visible row in one request
// instead of one GET per applicant (the source of the Postgres load). The response is
// keyed by applicant id with an entry for every requested id (empty array if none).
export async function getApplicantsAssignees(
    applicantIds: string[],
): Promise<Record<string, Assignee[]>> {
    return (
        await apiInstance.post<Record<string, Assignee[]>>(
            APIRoutes.APPLICANTS_ASSIGNEES,
            { applicantIds },
            {
                baseURL: `${Config.BaseApiUrl}/originations-review-portal`,
            },
        )
    ).data;
}

export async function postApplicantAssignee({
    applicantId,
    email,
    product,
}: AssigneeRequest): Promise<Assignee> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_ASSIGNEES, {
        applicantId,
    });
    return (
        await apiInstance.post<Assignee>(
            endpointUrl,
            { email, product },
            {
                baseURL: `${Config.BaseApiUrl}/originations-review-portal`,
            },
        )
    ).data;
}

export async function deleteApplicantAssignee({
    applicantId,
    assigneeId,
}: DeleteAssigneeRequest): Promise<void> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_ASSIGNEE_DELETE, {
        applicantId,
        assigneeId,
    });
    await apiInstance.delete(endpointUrl, {
        baseURL: `${Config.BaseApiUrl}/originations-review-portal`,
    });
}

export async function getAssignees(): Promise<string[]> {
    return (
        await apiInstance.get<string[]>(APIRoutes.ASSIGNEES, {
            baseURL: `${Config.BaseApiUrl}/originations-review-portal`,
        })
    ).data;
}
